// Colour maths for themes driven by each network's webpage settings (footer, sign-up page):
// parse settings colours and keep text readable (WCAG contrast) on any background.

export type Rgb = [number, number, number];

export function parseColor(value: string): Rgb | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const color = value.replace('!important', '').trim();
  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1];
    return [0, 2, 4].map(i => parseInt(h.substr(i, 2), 16)) as Rgb;
  }
  const rgb = color.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  return rgb ? [+rgb[1], +rgb[2], +rgb[3]] : null;
}

export function luminance([r, g, b]: Rgb): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrastRatio(l1: number, l2: number): number {
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export function isDark(color: string): boolean {
  const rgb = parseColor(color);
  return rgb ? luminance(rgb) < 0.4 : true;
}

/** rgba() from any parseable colour; unparseable values are returned as-is (still valid CSS). */
export function withAlpha(color: string, alpha: number): string {
  const rgb = parseColor(color);
  return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : color;
}

export function toHex([r, g, b]: Rgb): string {
  return '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase();
}

export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [0, 1, 2].map(i => a[i] + (b[i] - a[i]) * t) as Rgb;
}

export const WHITE: Rgb = [255, 255, 255];
export const BLACK: Rgb = [0, 0, 0];

/**
 * Readable text colour for bg, picked by actual contrast rather than a brightness cut-off, so
 * mid-tone brand colours (teal, pink, orange, grey) still get 4.5:1: white if it reaches 4.5:1,
 * else the softer near-black, else (true mid-tones only) whichever of pure white/black is higher.
 */
export function bestTextOn(bg: string): string {
  const b = parseColor(bg);
  if (!b) {
    return '#FFFFFF';
  }
  const l = luminance(b);
  const onWhite = contrastRatio(1, l);
  if (onWhite >= 4.5) {
    return '#FFFFFF';
  }
  if (contrastRatio(luminance([17, 17, 17]), l) >= 4.5) {
    return '#111111';
  }
  return onWhite >= contrastRatio(0, l) ? '#FFFFFF' : '#000000';
}

/** The colour itself when readable on bg, else nudged toward whichever of white/black reads better until it is. */
export function ensureContrast(color: string, bg: string, min = 4.5): string {
  const c = parseColor(color);
  const b = parseColor(bg);
  if (!c || !b) {
    return color;
  }
  const target = bestTextOn(bg) === '#FFFFFF' ? WHITE : BLACK;
  for (let t = 0; t <= 1.001; t += 0.05) {
    // Check the rounded hex actually returned, so rounding can't drop it just under min.
    const hex = toHex(mix(c, target, t));
    if (contrastRatio(luminance(parseColor(hex)), luminance(b)) >= min) {
      return hex;
    }
  }
  return toHex(target);
}
